import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { Icon } from './Icon';

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** Heading level for the title (defaults to h2 inside a page body). */
  as?: 'h2' | 'h3' | 'h4';
}

/** Calm, specific empty state: title + explanation + at most one action (design system §4.38). */
export function EmptyState({ icon, title, description, action, className, as: Heading = 'h2' }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center gap-3 px-4 py-12 text-center', className)}>
      {icon ? (
        <span className="flex size-16 items-center justify-center rounded-full bg-primary-subtle text-brand">
          <Icon icon={icon} size={28} />
        </span>
      ) : null}
      <Heading className="text-xl">{title}</Heading>
      {description ? <div className="max-w-reading text-sm text-muted">{description}</div> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export interface SkeletonProps {
  className?: string;
  /** Number of text lines to draw. */
  lines?: number;
}

/** Shapes mirror the final layout; shimmer is off under reduced motion / data saver. */
export function Skeleton({ className, lines }: SkeletonProps) {
  if (lines && lines > 1) {
    return (
      <div className={cn('flex flex-col gap-2', className)} aria-hidden="true">
        {Array.from({ length: lines }, (_, i) => (
          <div key={i} className="skeleton h-3" style={{ inlineSize: `${90 - ((i * 13) % 30)}%` }} />
        ))}
      </div>
    );
  }
  return <div className={cn('skeleton h-4 w-full', className)} aria-hidden="true" />;
}

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const ALERT: Record<AlertTone, { box: string; icon: LucideIcon }> = {
  info: { box: 'bg-info-subtle text-info-text border-info', icon: Info },
  success: { box: 'bg-success-subtle text-success-text border-success', icon: CircleCheck },
  warning: { box: 'bg-warning-subtle text-warning-text border-warning', icon: TriangleAlert },
  danger: { box: 'bg-danger-subtle text-danger-text border-danger', icon: CircleAlert },
};

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** role="alert" for errors that must be announced; status otherwise. */
  live?: boolean;
}

export function Alert({ tone = 'info', title, children, action, className, live }: AlertProps) {
  const spec = ALERT[tone];
  return (
    <div
      role={live ? (tone === 'danger' ? 'alert' : 'status') : undefined}
      className={cn('flex items-start gap-3 rounded-md border-s-4 p-3 text-sm', spec.box, className)}
    >
      <Icon icon={spec.icon} size={20} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={cn(title && 'mt-0.5')}>{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
